import React, { useMemo } from "react";
import clsx from "clsx";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { IconButton, Select, TextField } from "@components";
import IconTrash from "@assets/icon_trash.svg";
import IconDrag from "@assets/icon-drag.svg";
import { dictionary } from "@constants";
import { useAppSelector } from "@hooks";
import { createEducationNewPoint } from "@utils";
import { EDUCATION_NEW_TYPE_OPTIONS, fields } from "./localization";
import styles from "./EducationNewList.module.scss";
const MIN_YEAR = 1950;
const EXTRA_FUTURE_YEARS = 10;
const GRADUATION_YEAR_IN_PROGRESS_ID = "__education_new_graduation_in_progress__";
const GRADUATION_YEAR_IN_PROGRESS_VALUE = "In progress";
export function EducationNewList({ items, onEdit }) {
    const { language, isViewOnly } = useAppSelector((state) => state.ui);
    const field = fields(language);
    const { remove } = dictionary(language);
    const yearOptions = useMemo(() => {
        const maxYear = new Date().getFullYear() + EXTRA_FUTURE_YEARS;
        const years = Array.from({ length: maxYear - MIN_YEAR + 1 }, (_, index) => `${maxYear - index}`);
        return [
            { id: GRADUATION_YEAR_IN_PROGRESS_ID, label: field.graduationYearInProgress },
            ...years.map((year) => ({ id: year, label: year })),
        ];
    }, [field.graduationYearInProgress]);
    const educationTypeOptions = useMemo(() => [...EDUCATION_NEW_TYPE_OPTIONS]
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        .map((option) => ({ id: option, label: option })), []);
    const updateField = (index, key, value) => {
        onEdit(items.map((item, itemIndex) => (itemIndex === index ? Object.assign(Object.assign({}, item), { [key]: value }) : item)));
    };
    const onDragEnd = (result) => {
        if (!result.destination || result.destination.index === result.source.index || isViewOnly) {
            return;
        }
        const updated = [...items];
        const [removed] = updated.splice(result.source.index, 1);
        updated.splice(result.destination.index, 0, removed);
        onEdit(updated);
    };
    const onRemove = (index) => {
        if (items.length <= 1) {
            onEdit([createEducationNewPoint()]);
            return;
        }
        onEdit(items.filter((_, itemIndex) => itemIndex !== index));
    };
    return (React.createElement(DragDropContext, { onDragEnd: onDragEnd },
        React.createElement(Droppable, { droppableId: "education-new-list", type: "education-new" }, (provided) => (React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.droppableProps, { className: styles.container }),
            items.map((item, index) => {
                const isSingleEmpty = items.length === 1 &&
                    !item.educationType &&
                    !item.institutionName &&
                    !item.specialization &&
                    !item.graduationOrExpected;
                return (React.createElement(Draggable, { key: item.displayId, draggableId: item.displayId, index: index, isDragDisabled: isViewOnly || items.length < 2 }, (draggableProvided, snapshot) => (React.createElement("div", Object.assign({ ref: draggableProvided.innerRef }, draggableProvided.draggableProps, { className: styles.itemRow }),
                    React.createElement("div", { className: clsx(styles.item, snapshot.isDragging && styles.isDragging) },
                        React.createElement("div", Object.assign({ className: styles.dragIconContainer }, draggableProvided.dragHandleProps),
                            React.createElement(IconDrag, { className: clsx(styles.drag, (isViewOnly || items.length < 2) && styles.dragDisabled) })),
                        React.createElement("div", { className: styles.fields },
                            React.createElement(Select, { label: field.educationType, value: item.educationType, options: educationTypeOptions.map((option) => (Object.assign(Object.assign({}, option), { isSelected: option.label === item.educationType }))), onOptionClick: (_, option) => {
                                    updateField(index, "educationType", option.label);
                                }, placeholder: field.educationTypePlaceholder, disabled: isViewOnly, isSelectable: true }),
                            React.createElement(Select, { label: field.graduationOrExpected, value: item.graduationOrExpected.trim() &&
                                    item.graduationOrExpected !== GRADUATION_YEAR_IN_PROGRESS_VALUE
                                    ? item.graduationOrExpected
                                    : field.graduationYearInProgress, options: yearOptions.map((option) => (Object.assign(Object.assign({}, option), { isSelected: option.id === GRADUATION_YEAR_IN_PROGRESS_ID
                                        ? !item.graduationOrExpected.trim() ||
                                            item.graduationOrExpected === GRADUATION_YEAR_IN_PROGRESS_VALUE ||
                                            item.graduationOrExpected === field.graduationYearInProgress
                                        : option.label === item.graduationOrExpected }))), onOptionClick: (_, option) => {
                                    if (option.id === GRADUATION_YEAR_IN_PROGRESS_ID) {
                                        updateField(index, "graduationOrExpected", GRADUATION_YEAR_IN_PROGRESS_VALUE);
                                    }
                                    else {
                                        updateField(index, "graduationOrExpected", option.label);
                                    }
                                }, placeholder: field.graduationYearPlaceholder, disabled: isViewOnly, isSelectable: true, hasInput: true }),
                            React.createElement("div", { className: styles.fieldFullRow },
                                React.createElement(TextField, { label: field.institutionName, value: item.institutionName, placeholder: field.institutionNamePlaceholder, onChange: ({ target: { value } }) => updateField(index, "institutionName", value), maxLength: 90, isLimitVisible: true, disabled: isViewOnly })),
                            React.createElement("div", { className: styles.fieldFullRow },
                                React.createElement(TextField, { label: field.specialization, value: item.specialization, placeholder: field.specializationPlaceholder, onChange: ({ target: { value } }) => updateField(index, "specialization", value), maxLength: 90, isLimitVisible: true, disabled: isViewOnly })))),
                    React.createElement(IconButton, { className: styles.removeButton, color: "secondary", onClick: () => onRemove(index), icon: React.createElement(IconTrash, null), disabled: isViewOnly || isSingleEmpty, title: remove })))));
            }),
            provided.placeholder)))));
}
